import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../store/store';
import { selectUser } from '../store/slices/authSlice';
import axios from 'axios';
import GlobalRoleManager from '../components/GlobalRoleManager';
import { useSocket } from '../hooks/useSocket';
import { API_BASE_URL } from '../config/api';
import { 
  CheckCircle2,
  AlertCircle,
  Circle,
  Lock,
  MoreVertical,
  Plus,
  Calendar as CalendarIcon,
  Users,
  Grid3x3,
  ChevronDown,
  X,
  Clock,
  Star,
  BarChart2,
  List,
  Globe,
  ArrowRight,
  TrendingUp,
  Target,
  Zap,
  Award,
  Activity,
  Briefcase,
  GanttChart,
  ExternalLink,
  User,
  FolderKanban,
  Bell,
  Mail,
  Check,
  Store,
  X as XIcon
} from 'lucide-react';

const API_BASE = API_BASE_URL;

interface TaskData {
  _id: string;
  title: string;
  description: string;
  status: 'todo' | 'in-progress' | 'review' | 'done' | 'blocked' | 'archived';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  deadline: string | null;
  startDate?: string;
  duration?: number;
  project: { 
    _id: string; 
    title: string;
    creator?: { _id: string; name?: string; email?: string } | string;
  };
  team: { _id: string; name: string };
  assignee: { _id: string; name: string; email: string } | null;
}

interface ProjectData {
  _id: string;
  title: string;
  description?: string;
  type: 'meeting' | 'workshop' | 'deadline' | 'social';
  start: string;
  end: string;
  color?: string;
  isPublic?: boolean;
  teams?: any[];
  creator?: { _id: string; name: string; email: string };
}

interface TeamData {
  _id: string;
  name: string;
  description: string;
  members: { _id: string; name: string; email: string }[];
  creator: { _id: string; name: string; email: string };
}

const Dashboard: React.FC = () => {
  const user = useSelector((state: RootState) => selectUser(state));
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const { socket } = useSocket(token);

  const [loading, setLoading] = useState(true);
  const [tasks, setTasks] = useState<TaskData[]>([]);
  const [projects, setProjects] = useState<ProjectData[]>([]);
  const [teams, setTeams] = useState<TeamData[]>([]);
  const [activeTaskTab, setActiveTaskTab] = useState<'upcoming' | 'overdue' | 'completed'>('upcoming');
  const [completedTasksCount, setCompletedTasksCount] = useState(0);
  const [overdueTasksCount, setOverdueTasksCount] = useState(0);
  const [projectViewMode, setProjectViewMode] = useState<'list'>('list');
  const [showCreateProjectModal, setShowCreateProjectModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState<TaskData | null>(null);
  const [showTaskDetailsModal, setShowTaskDetailsModal] = useState(false);
  const [showCreateTaskModal, setShowCreateTaskModal] = useState(false);
  const [taskFormData, setTaskFormData] = useState({
    title: '',
    description: '',
    status: 'todo' as 'todo' | 'in-progress' | 'review' | 'done' | 'blocked',
    priority: 'medium' as 'low' | 'medium' | 'high' | 'urgent',
    deadline: '',
    projectId: '',
    teamId: '',
    assigneeId: '',
    tags: [] as string[],
    subTasks: [] as Array<{ title: string; assigneeId?: string; deadline?: string }>
  });
  const [tagsInput, setTagsInput] = useState('');
  const [projectTeamsWithMembers, setProjectTeamsWithMembers] = useState<Record<string, any[]>>({});
  const [teamInvitations, setTeamInvitations] = useState<any[]>([]);
  const [notificationHistory, setNotificationHistory] = useState<any[]>([]);
  const [showNotificationsDropdown, setShowNotificationsDropdown] = useState(false);
  const [projectFormData, setProjectFormData] = useState({
    title: '',
    description: '',
    start: '',
    end: '',
    type: 'meeting' as 'meeting' | 'workshop' | 'deadline' | 'social',
    color: '#3788d8',
    isPublic: true
  });

  // Get current date and greeting
  const getCurrentDate = () => {
    const now = new Date();
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    return `${days[now.getDay()]}, ${months[now.getMonth()]} ${now.getDate()}`;
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  };

  const getFirstName = (name: string) => {
    return name?.split(' ')[0] || 'User';
  };

  // Helper to normalize date (set to midnight for day comparison)
  const normalizeDate = (date: Date | string) => {
    const normalized = new Date(date);
    normalized.setHours(0, 0, 0, 0);
    return normalized;
  };

  // Fetch all data
  useEffect(() => {
    if (user?._id) {
      fetchDashboardData();
    }
  }, [user?._id]);

  // Close notifications dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (showNotificationsDropdown && !target.closest('.notifications-dropdown-container')) {
        setShowNotificationsDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showNotificationsDropdown]);

  // Écouter les notifications Socket.io en temps réel
  useEffect(() => {
    if (!socket) return;

    const handleNotification = (notification: any) => {
      console.log('📬 Notification reçue:', notification);
      
      // Si c'est une notification de tâche assignée
      if (notification.type === 'task_assigned') {
        setNotificationHistory((prev) => {
          // Éviter les doublons
          const exists = prev.some((n: any) => n._id === notification._id || 
            (n.relatedId === notification.relatedId && n.type === 'task_assigned'));
          if (exists) return prev;
          
          return [{ ...notification, timestamp: new Date() }, ...prev];
        });
      } else if (notification.type === 'team_invite') {
        // Si c'est une invitation d'équipe, rafraîchir les invitations
        fetchDashboardData();
      }
    };

    (socket as any).on('notification', handleNotification);

    return () => {
      (socket as any).off('notification', handleNotification);
    };
  }, [socket]);

  const fetchDashboardData = async () => {
    if (!user?._id) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      // Fetch projects (this includes teams linked to projects)
      const projectsRes = await axios.get(`${API_BASE}/projects?userId=${user._id}`, { headers });
      const fetchedProjects = projectsRes.data.data || [];
      setProjects(fetchedProjects);

      // Fetch teams
      const teamsRes = await axios.get(`${API_BASE}/teams/my-teams-events`, { headers });
      const teamsData = teamsRes.data.data || {};
      const fetchedTeams = teamsData.teams || [];
      
      // Also get teams from projects (projects have teams array)
      const projectTeams: TeamData[] = [];
      fetchedProjects.forEach((project: ProjectData) => {
        if (project.teams && Array.isArray(project.teams)) {
          project.teams.forEach((team: any) => {
            // Avoid duplicates
            if (!fetchedTeams.find((t: TeamData) => t._id === team._id) &&
                !projectTeams.find((t: TeamData) => t._id === team._id)) {
              projectTeams.push(team);
            }
          });
        }
      });
      
      // Combine all teams
      const allTeams = [...fetchedTeams, ...projectTeams];
      setTeams(allTeams);


      // Fetch all tasks from user's teams (including teams from projects)
      const allTasks: TaskData[] = [];
      for (const team of allTeams) {
        try {
          const taskRes = await axios.get(`${API_BASE}/tasks/team/${team._id}`, { headers });
          const teamTasks = taskRes.data.data?.tasks || [];
          console.log(`Fetched ${teamTasks.length} tasks from team ${team._id}`, teamTasks);
          allTasks.push(...teamTasks);
        } catch (err) {
          console.error(`Error fetching tasks for team ${team._id}:`, err);
        }
      }

      // Also fetch tasks from projects
      for (const project of fetchedProjects) {
        try {
          const taskRes = await axios.get(`${API_BASE}/tasks/${project._id}?userId=${user._id}`, { headers });
          const responseData = taskRes.data?.data;
          const projectTasks = responseData?.tasks || (Array.isArray(responseData) ? responseData : []);
          console.log(`Fetched ${projectTasks.length} tasks from project ${project._id}`, projectTasks);
          allTasks.push(...projectTasks);
        } catch (err) {
          console.error(`Error fetching tasks for project ${project._id}:`, err);
        }
      }

      console.log(`Total tasks fetched: ${allTasks.length}`, allTasks);
      console.log(`User ID: ${user._id}`);
      console.log(`All teams (including from projects):`, allTeams.map((t: TeamData) => ({ id: t._id, name: t.name, members: t.members })));
      console.log(`Projects:`, fetchedProjects.map((p: ProjectData) => ({ id: p._id, title: p.title, teams: p.teams?.length || 0 })));

      // Filter tasks to only include those assigned to the current user
      const userTasks = allTasks.filter(task => {
        const assigneeId = typeof task.assignee === 'object' 
          ? task.assignee?._id 
          : task.assignee;
        return assigneeId && assigneeId.toString() === user._id.toString();
      });
      
      console.log(`Tasks assigned to user ${user._id}: ${userTasks.length}`, userTasks);

      // Remove duplicates by _id
      const uniqueTasks = Array.from(
        new Map(userTasks.map(task => [task._id, task])).values()
      );

      console.log(`Unique tasks after deduplication: ${uniqueTasks.length}`, uniqueTasks);
      setTasks(uniqueTasks);

      // Calculate counts
      const now = normalizeDate(new Date());
      const completed = uniqueTasks.filter(t => t.status === 'done').length;
      const overdue = uniqueTasks.filter(t => {
        if (!t.deadline || t.status === 'done') return false;
        try {
          const deadlineDate = normalizeDate(new Date(t.deadline));
          return deadlineDate < now;
        } catch (e) {
          return false;
        }
      }).length;

      setCompletedTasksCount(completed);
      setOverdueTasksCount(overdue);

      // Fetch team invitations for the current user using dedicated endpoint
      try {
        const invitationsRes = await axios.get(`${API_BASE}/teams/my-invitations`, { headers });
        const invitations = invitationsRes.data.data || [];
        console.log('Team invitations found:', invitations.length, invitations);
        setTeamInvitations(invitations);
      } catch (inviteErr) {
        console.error('Error fetching invitations:', inviteErr);
        // If endpoint fails, try fallback method
        try {
          const invitations: any[] = [];
          for (const team of allTeams) {
            if (team.invites && Array.isArray(team.invites)) {
              const pendingInvites = team.invites.filter((inv: any) => 
                inv.status === 'pending' && inv.email?.toLowerCase() === user?.email?.toLowerCase()
              );
              pendingInvites.forEach((invite: any) => {
                invitations.push({
                  ...invite,
                  teamId: team._id,
                  teamName: team.name,
                  team: team
                });
              });
            }
          }
          setTeamInvitations(invitations);
        } catch (fallbackErr) {
          console.error('Error in fallback invitation fetch:', fallbackErr);
        }
      }

    } catch (err: any) {
      console.error('Error fetching dashboard data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Handle accept invitation
  const handleAcceptInvitation = async (token: string) => {
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      await axios.post(`${API_BASE}/teams/accept`, { token }, { headers });
      // Refresh dashboard data
      await fetchDashboardData();
    } catch (err: any) {
      console.error('Error accepting invitation:', err);
      alert(err.response?.data?.error || 'Error accepting invitation');
    }
  };

  // Handle reject invitation
  const handleRejectInvitation = async (token: string) => {
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      await axios.post(`${API_BASE}/teams/reject`, { token }, { headers });
      // Refresh dashboard data
      await fetchDashboardData();
    } catch (err: any) {
      console.error('Error rejecting invitation:', err);
      alert(err.response?.data?.error || 'Error rejecting invitation');
    }
  };

  // Filter tasks based on active tab
  const getFilteredTasks = () => {
    const now = normalizeDate(new Date());
    console.log(`Filtering tasks for tab: ${activeTaskTab}, Total tasks: ${tasks.length}`, tasks);
    
    let filtered: TaskData[] = [];
    switch (activeTaskTab) {
      case 'upcoming':
        filtered = tasks.filter(t => {
          if (t.status === 'done') return false;
          if (!t.deadline) return true; // Include tasks without deadline in upcoming
          try {
            const deadlineDate = normalizeDate(new Date(t.deadline));
            return deadlineDate >= now;
          } catch (e) {
            console.error('Error parsing deadline:', t.deadline, e);
            return true; // Include if date parsing fails
          }
        });
        break;
      case 'overdue':
        filtered = tasks.filter(t => {
          if (t.status === 'done') return false;
          if (!t.deadline) return false;
          try {
            const deadlineDate = normalizeDate(new Date(t.deadline));
            return deadlineDate < now;
          } catch (e) {
            console.error('Error parsing deadline:', t.deadline, e);
            return false;
          }
        });
        break;
      case 'completed':
        filtered = tasks.filter(t => t.status === 'done');
        break;
      default:
        filtered = [];
    }
    
    console.log(`Filtered tasks for ${activeTaskTab}: ${filtered.length}`, filtered);
    return filtered;
  };

  const getTaskIcon = (task: TaskData) => {
    if (task.status === 'done') {
      return <CheckCircle2 className="h-4 w-4 text-green-500" />;
    }
    if (task.priority === 'urgent') {
      return <AlertCircle className="h-4 w-4 text-red-500" />;
    }
    if (task.priority === 'high') {
      return <AlertCircle className="h-4 w-4 text-orange-500" />;
    }
    return <Circle className="h-4 w-4 text-yellow-500" />;
  };

  const getProjectIcon = (project: ProjectData) => {
    switch (project.type) {
      case 'meeting':
        return <BarChart2 className="h-5 w-5 text-purple-500" />;
      case 'workshop':
        return <BarChart2 className="h-5 w-5 text-blue-500" />;
      case 'deadline':
        return <Star className="h-5 w-5 text-green-500" />;
      default:
        return <BarChart2 className="h-5 w-5 text-gray-400" />;
    }
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      return date.toLocaleDateString('en-US', { day: 'numeric', month: 'short' });
    } catch {
      return '';
    }
  };

  const getProjectTeamName = (project: ProjectData) => {
    if (project.teams && project.teams.length > 0) {
      return project.teams[0]?.name || `${getFirstName(user?.name || 'User')}'s team`;
    }
    return `${getFirstName(user?.name || 'User')}'s team`;
  };

  const getTasksDueSoon = (project: ProjectData) => {
    const projectTasks = tasks.filter(t => t.project?._id === project._id);
    const now = new Date();
    const sevenDaysFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
    const dueSoon = projectTasks.filter(t => {
      if (!t.deadline || t.status === 'done') return false;
      const deadline = new Date(t.deadline);
      return deadline >= now && deadline <= sevenDaysFromNow;
    }).length;
    return dueSoon > 0 ? `${dueSoon} tasks due soon` : '';
  };

  // Project helper functions
  const formatProjectDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      day: 'numeric', 
      month: 'short' 
    });
  };

  const formatProjectTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  const calculateProjectDuration = (start: string, end: string) => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    const diff = endDate.getTime() - startDate.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    if (hours > 0) {
      return `${hours}h${minutes > 0 ? ` ${minutes}min` : ''}`;
    }
    return `${minutes}min`;
  };

  const getTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      meeting: 'Meeting',
      workshop: 'Workshop',
      deadline: 'Deadline',
      social: 'Social'
    };
    return labels[type] || type;
  };

  const getTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      meeting: 'bg-blue-100 text-blue-800 border-blue-300',
      workshop: 'bg-purple-100 text-purple-800 border-purple-300',
      deadline: 'bg-orange-100 text-orange-800 border-orange-300',
      social: 'bg-purple-200 text-purple-800 border-purple-300'
    };
    return colors[type] || colors.meeting;
  };

  // Task creation handler
  const handleCreateTask = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?._id || !taskFormData.projectId) {
      alert('Please select a project');
      return;
    }

    // Assignee is optional now (can be "Aucun")

    // Get the project to find its team
    const selectedProject = projects.find(p => p._id === taskFormData.projectId);
    if (!selectedProject) {
      alert('Project not found');
      return;
    }

    // Try to get team from cache first, then from project, then from teams list
    let teamId: string | undefined;
    let selectedTeam: any = null;

    // First, try from cache (teams with members)
    const cachedTeams = projectTeamsWithMembers[taskFormData.projectId];
    if (cachedTeams && cachedTeams.length > 0) {
      teamId = cachedTeams[0]._id;
      selectedTeam = cachedTeams[0];
      console.log('Team found in cache:', teamId);
    }
    
    // If not in cache, try from project.teams
    if (!teamId && selectedProject.teams && selectedProject.teams.length > 0) {
      const teamFromProject = selectedProject.teams[0];
      teamId = typeof teamFromProject === 'object' ? teamFromProject._id : teamFromProject;
      selectedTeam = typeof teamFromProject === 'object' ? teamFromProject : null;
      console.log('Team found in project.teams:', teamId);
    }
    
    // If still not found, try to find team from teams list that is linked to this project
    if (!teamId) {
      const linkedTeam = teams.find((t: any) => {
        // Check if team is linked to this project via linkedProjectId
        const teamLinkedProjectId = typeof t.linkedProjectId === 'object' 
          ? t.linkedProjectId?._id 
          : t.linkedProjectId;
        return teamLinkedProjectId === taskFormData.projectId;
      });
      if (linkedTeam) {
        teamId = linkedTeam._id;
        selectedTeam = linkedTeam;
        console.log('Team found in teams list via linkedProjectId:', teamId);
      }
    }

    // If still no team found, try to fetch it from the API
    if (!teamId) {
      try {
        console.log('Fetching project teams from API...');
        const token = localStorage.getItem('token');
        const headers = { Authorization: `Bearer ${token}` };
        const res = await axios.get(`${API_BASE}/projects/${taskFormData.projectId}`, { headers });
        const projectWithTeams = res.data.data;
        if (projectWithTeams?.teams && projectWithTeams.teams.length > 0) {
          teamId = projectWithTeams.teams[0]._id;
          selectedTeam = projectWithTeams.teams[0];
          // Cache it for future use
          setProjectTeamsWithMembers(prev => ({
            ...prev,
            [taskFormData.projectId]: projectWithTeams.teams
          }));
          console.log('Team found via API and cached:', teamId);
        }
      } catch (err) {
        console.error('Error fetching project teams:', err);
      }
    }

    if (!teamId || !selectedTeam) {
      console.error('No team found. Project:', selectedProject);
      console.error('Available teams:', teams.map((t: any) => ({ 
        id: t._id, 
        name: t.name, 
        linkedProjectId: t.linkedProjectId 
      })));
      alert('No team found for this project. Please ensure the project has a team.');
      return;
    }

    // Verify that the selected assignee is a member of the project's team
    const isMember = selectedTeam?.members?.some((m: any) => 
      (typeof m === 'object' ? m._id : m) === taskFormData.assigneeId
    );
    
    if (!isMember) {
      alert('Selected member is not part of the project team');
      return;
    }

    setLoading(true);
    try {
      // Build payload according to backend API expectations
      const payload: any = {
        title: taskFormData.title,
        description: taskFormData.description || '',
        status: taskFormData.status,
        priority: taskFormData.priority,
        assignee: taskFormData.assigneeId || null, // Can be null if "Aucun"
      };

      if (taskFormData.deadline) {
        payload.deadline = taskFormData.deadline;
      }
      
      // Add tags if any
      if (taskFormData.tags.length > 0) {
        payload.tags = taskFormData.tags.map(tag => tag.trim().toLowerCase());
      }

      // Add subTasks if any
      if (taskFormData.subTasks.length > 0) {
        payload.subTasks = taskFormData.subTasks
          .filter(st => st.title.trim())
          .map(st => ({
            title: st.title.trim(),
            assignee: st.assigneeId || undefined,
            deadline: st.deadline || undefined
          }));
      }

      // Use the correct endpoint: /api/tasks/team/:teamId
      const res = await axios.post(`${API_BASE}/tasks/team/${teamId}`, payload, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      // Refresh tasks
      await fetchDashboardData();

      setShowCreateTaskModal(false);
      setTaskFormData({
        title: '',
        description: '',
        status: 'todo',
        priority: 'medium',
        deadline: '',
        projectId: '',
        teamId: '',
        assigneeId: '',
        tags: [],
        subTasks: []
      });
      setTagsInput('');
    } catch (err: any) {
      console.error('Error creating task:', err);
      alert(err.response?.data?.error || 'Error creating task');
    } finally {
      setLoading(false);
    }
  };

  // Project creation handler
  const handleCreateProject = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?._id) return;
    setLoading(true);
    try {
      const payload = { ...projectFormData, creatorId: user._id };
      const res = await axios.post(`${API_BASE}/projects`, payload, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setProjects((prev) => [...prev, res.data.data]);
      setShowCreateProjectModal(false);
      setProjectFormData({
        title: '',
        description: '',
        start: '',
        end: '',
        type: 'meeting',
        color: '#3788d8',
        isPublic: true
      });
    } catch (err: any) {
      console.error('Error creating project:', err);
    } finally {
      setLoading(false);
    }
  };

  // Update color when type changes
  useEffect(() => {
    const colors: Record<string, string> = {
      meeting: '#3788d8',
      workshop: '#56cc9d',
      deadline: '#ff9f40',
      social: '#9333ea'
    };
    const defaultColor = colors[projectFormData.type] || '#3788d8';
    setProjectFormData(prev => ({ ...prev, color: defaultColor }));
  }, [projectFormData.type]);

  // Project statistics
  const projectStats = {
    meeting: projects.filter((p) => p.type === 'meeting').length,
    workshop: projects.filter((p) => p.type === 'workshop').length,
    deadline: projects.filter((p) => p.type === 'deadline').length,
    social: projects.filter((p) => p.type === 'social').length,
  };

  const handleProjectClick = (projectId: string) => {
    navigate(`/projects/${projectId}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-gray-900">Loading...</div>
      </div>
    );
  }

  // Calculate statistics
  const totalTasks = tasks.length;
  const upcomingTasks = getFilteredTasks().length;
  const activeProjects = projects.filter(p => new Date(p.end) >= new Date()).length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
      {/* Enhanced Header with Stats */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-8 py-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <p className="text-gray-500 text-sm mb-1">{getCurrentDate()}</p>
              <h1 className="text-3xl font-bold text-gray-900">
                {getGreeting()}, {getFirstName(user?.name || 'User')} 👋
              </h1>
            </div>
            <div className="flex items-center gap-3">
              {/* Manage business — moved here from the sidebar */}
              <button
                onClick={() => navigate('/dashboard/create-business')}
                className="hidden sm:flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 transition-colors shadow-sm"
              >
                <Store className="h-4 w-4" />
                <span>My business</span>
              </button>
              {/* Notifications Bell Icon with Dropdown */}
              <div className="relative notifications-dropdown-container">
                <button
                  onClick={() => setShowNotificationsDropdown(!showNotificationsDropdown)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors relative"
                >
                  <Bell className="h-5 w-5 text-gray-600" />
                  {(teamInvitations.length > 0 || notificationHistory.length > 0) && (
                    <span className="absolute -top-1 -right-1 h-5 w-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold">
                      {teamInvitations.length + notificationHistory.filter((n: any) => !n.read).length}
                    </span>
                  )}
                </button>
                
                {/* Notifications Dropdown */}
                {showNotificationsDropdown && (
                  <div className="absolute right-0 mt-2 w-96 bg-white rounded-xl shadow-2xl border border-gray-200 z-50 max-h-[600px] overflow-hidden flex flex-col notifications-dropdown-container">
                    <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-purple-50">
                      <div className="flex items-center justify-between">
                        <h3 className="text-lg font-semibold text-gray-900">Notifications</h3>
                        <button
                          onClick={() => setShowNotificationsDropdown(false)}
                          className="text-gray-400 hover:text-gray-600"
                        >
                          <X className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                    
                    <div className="overflow-y-auto flex-1">
                      {/* Pending Invitations */}
                      {teamInvitations.length > 0 && (
                        <div className="p-4">
                          <h4 className="text-sm font-semibold text-gray-700 mb-3">Pending Invitations</h4>
                          <div className="space-y-2">
                            {teamInvitations.map((invitation: any, index: number) => (
                              <div
                                key={index}
                                className="bg-blue-50 border border-blue-200 rounded-lg p-3"
                              >
                                <div className="flex items-start justify-between mb-2">
                                  <div className="flex-1">
                                    <p className="text-sm font-medium text-gray-900">
                                      Invitation to join <span className="text-blue-600">{invitation.teamName}</span>
                                    </p>
                                    <p className="text-xs text-gray-500 mt-1">You've been invited to collaborate</p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2 mt-2">
                                  <button
                                    onClick={async () => {
                                      await handleAcceptInvitation(invitation.token);
                                      // Move to history
                                      setNotificationHistory(prev => [...prev, { ...invitation, action: 'accepted', timestamp: new Date() }]);
                                      setTeamInvitations(prev => prev.filter((inv: any) => inv.token !== invitation.token));
                                    }}
                                    className="flex-1 px-3 py-1.5 bg-green-500 hover:bg-green-600 text-white rounded text-xs font-medium flex items-center justify-center gap-1 transition-colors"
                                  >
                                    <Check className="h-3 w-3" />
                                    Accept
                                  </button>
                                  <button
                                    onClick={async () => {
                                      await handleRejectInvitation(invitation.token);
                                      // Move to history
                                      setNotificationHistory(prev => [...prev, { ...invitation, action: 'rejected', timestamp: new Date() }]);
                                      setTeamInvitations(prev => prev.filter((inv: any) => inv.token !== invitation.token));
                                    }}
                                    className="flex-1 px-3 py-1.5 bg-red-500 hover:bg-red-600 text-white rounded text-xs font-medium flex items-center justify-center gap-1 transition-colors"
                                  >
                                    <XIcon className="h-3 w-3" />
                                    Decline
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Notification History */}
                      {notificationHistory.length > 0 && (
                        <div className="p-4 border-t border-gray-200">
                          <h4 className="text-sm font-semibold text-gray-700 mb-3">History</h4>
                          <div className="space-y-2">
                            {notificationHistory.map((item: any, index: number) => {
                              // Notification de tâche assignée
                              if (item.type === 'task_assigned') {
                                return (
                                  <div
                                    key={index}
                                    className="bg-purple-50 border border-purple-200 rounded-lg p-3 hover:bg-purple-100 transition-colors cursor-pointer"
                                    onClick={() => {
                                      if (item.relatedId && item.relatedType === 'task') {
                                        // Naviguer vers la tâche si possible
                                        // Pour l'instant, on peut juste fermer la notification
                                        setNotificationHistory(prev => prev.filter((_, i) => i !== index));
                                      }
                                    }}
                                  >
                                    <div className="flex items-start justify-between">
                                      <div className="flex-1">
                                        <p className="text-sm font-semibold text-purple-900 mb-1">
                                          {item.title || 'New task assigned'}
                                        </p>
                                        <p className="text-xs text-purple-700">
                                          {item.message || 'A new task has been assigned to you'}
                                        </p>
                                        <p className="text-xs text-purple-500 mt-1">
                                          {item.timestamp ? new Date(item.timestamp).toLocaleString() : 
                                           item.createdAt ? new Date(item.createdAt).toLocaleString() : 'Just now'}
                                        </p>
                                      </div>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setNotificationHistory(prev => prev.filter((_, i) => i !== index));
                                        }}
                                        className="text-purple-400 hover:text-purple-600 ml-2"
                                      >
                                        <XIcon className="h-4 w-4" />
                                      </button>
                                    </div>
                                  </div>
                                );
                              }
                              
                              // Notification d'invitation (ancien format)
                              return (
                              <div
                                key={index}
                                className={`border rounded-lg p-3 ${
                                  item.action === 'accepted' 
                                    ? 'bg-green-50 border-green-200' 
                                    : 'bg-gray-50 border-gray-200'
                                }`}
                              >
                                <div className="flex items-start justify-between">
                                  <div className="flex-1">
                                    <p className="text-sm text-gray-900">
                                      {item.action === 'accepted' ? 'Accepted' : 'Declined'} invitation to <span className="font-medium">{item.teamName}</span>
                                    </p>
                                    <p className="text-xs text-gray-500 mt-1">
                                      {item.timestamp ? new Date(item.timestamp).toLocaleString() : 'Just now'}
                                    </p>
                                  </div>
                                  <button
                                    onClick={() => {
                                      setNotificationHistory(prev => prev.filter((_, i) => i !== index));
                                    }}
                                    className="text-gray-400 hover:text-gray-600 ml-2"
                                  >
                                    <XIcon className="h-4 w-4" />
                                  </button>
                                </div>
                              </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      
                      {teamInvitations.length === 0 && notificationHistory.length === 0 && (
                        <div className="p-8 text-center text-gray-500 text-sm">
                          No notifications
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              <button className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors">
                <span className="text-sm font-medium text-gray-700">My week</span>
                <ChevronDown className="h-4 w-4 text-gray-500" />
              </button>
              <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <Grid3x3 className="h-5 w-5 text-gray-600" />
              </button>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-gradient-to-br from-blue-300 to-blue-400 rounded-xl p-4 text-white shadow-lg hover:shadow-xl transition-shadow">
              <div className="flex items-center justify-between mb-2">
                <Briefcase className="h-5 w-5 opacity-90" />
                <TrendingUp className="h-4 w-4 opacity-75" />
              </div>
              <p className="text-2xl font-bold">{activeProjects}</p>
              <p className="text-sm opacity-90">Active Projects</p>
            </div>

            <div className="bg-gradient-to-br from-purple-300 to-purple-400 rounded-xl p-4 text-white shadow-lg hover:shadow-xl transition-shadow">
              <div className="flex items-center justify-between mb-2">
                <Target className="h-5 w-5 opacity-90" />
                <Activity className="h-4 w-4 opacity-75" />
              </div>
              <p className="text-2xl font-bold">{upcomingTasks}</p>
              <p className="text-sm opacity-90">Upcoming Tasks</p>
            </div>

            <div className="bg-gradient-to-br from-orange-300 to-orange-400 rounded-xl p-4 text-white shadow-lg hover:shadow-xl transition-shadow">
              <div className="flex items-center justify-between mb-2">
                <AlertCircle className="h-5 w-5 opacity-90" />
                <Zap className="h-4 w-4 opacity-75" />
              </div>
              <p className="text-2xl font-bold">{overdueTasksCount}</p>
              <p className="text-sm opacity-90">Overdue Tasks</p>
            </div>

            <div className="bg-gradient-to-br from-green-300 to-green-400 rounded-xl p-4 text-white shadow-lg hover:shadow-xl transition-shadow">
              <div className="flex items-center justify-between mb-2">
                <Award className="h-5 w-5 opacity-90" />
                <CheckCircle2 className="h-4 w-4 opacity-75" />
              </div>
              <p className="text-2xl font-bold">{completedTasksCount}</p>
              <p className="text-sm opacity-90">Completed</p>
            </div>
          </div>
        </div>
      </div>

      {/* Global Role Manager Section */}
      <div className="max-w-7xl mx-auto px-8 py-4">
        {user?._id && (
          <GlobalRoleManager 
            userId={user._id} 
            currentGlobalRole={user.globalRole}
            onUpdate={async () => {
              // Rafraîchir les données utilisateur si nécessaire
              try {
                const res = await axios.get(`${API_BASE}/users/profile`, {
                  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                });
                if (res.data.success) {
                  // Le rôle global sera mis à jour dans le store via le système d'auth
                }
              } catch (err) {
                console.error('Erreur rafraîchissement profil:', err);
              }
            }}
          />
        )}
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-8 py-6 grid grid-cols-12 gap-6">
        {/* My Tasks Section - Large, top-left */}
        <div className="col-span-12 lg:col-span-8 bg-white rounded-2xl border border-gray-200 shadow-lg hover:shadow-xl transition-shadow">
          {/* Tasks Header */}
          <div className="border-b border-gray-200 p-5 bg-gradient-to-r from-purple-50 to-blue-50 rounded-t-2xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center text-sm font-bold text-white shadow-md">
                  {getFirstName(user?.name || 'User')[0]?.toUpperCase()}
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">My tasks</h2>
                  <p className="text-xs text-gray-500 mt-0.5">{totalTasks} total tasks</p>
                </div>
              </div>
              <button className="p-2 hover:bg-white/50 rounded-lg transition-colors">
                <MoreVertical className="h-5 w-5 text-gray-500" />
              </button>
            </div>
          </div>

          {/* Enhanced Tabs */}
          <div className="flex border-b border-gray-200 bg-gray-50/50">
            <button
              onClick={() => setActiveTaskTab('upcoming')}
              className={`px-6 py-3 text-sm font-semibold transition-all relative ${
                activeTaskTab === 'upcoming'
                  ? 'text-blue-600 bg-white'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-white/50'
              }`}
            >
              <span className="flex items-center gap-2">
                <Target className="h-4 w-4" />
                Upcoming
              </span>
              {activeTaskTab === 'upcoming' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600"></div>
              )}
            </button>
            <button
              onClick={() => setActiveTaskTab('overdue')}
              className={`px-6 py-3 text-sm font-semibold transition-all relative ${
                activeTaskTab === 'overdue'
                  ? 'text-orange-600 bg-white'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-white/50'
              }`}
            >
              <span className="flex items-center gap-2">
                <AlertCircle className="h-4 w-4" />
                Overdue
                {overdueTasksCount > 0 && (
                  <span className="ml-1 px-2 py-0.5 bg-orange-100 text-orange-700 rounded-full text-xs font-bold">
                    {overdueTasksCount}
                  </span>
                )}
              </span>
              {activeTaskTab === 'overdue' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-orange-600"></div>
              )}
            </button>
            <button
              onClick={() => setActiveTaskTab('completed')}
              className={`px-6 py-3 text-sm font-semibold transition-all relative ${
                activeTaskTab === 'completed'
                  ? 'text-green-600 bg-white'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-white/50'
              }`}
            >
              <span className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4" />
                Completed
              </span>
              {activeTaskTab === 'completed' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-green-600"></div>
              )}
            </button>
          </div>

          {/* Create Task Button */}
          <div className="p-4 border-b border-gray-200 bg-gray-50/30">
            <button
              onClick={() => setShowCreateTaskModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all shadow-md hover:shadow-lg font-medium"
            >
              <Plus className="h-4 w-4" />
              <span className="text-sm">Create task</span>
            </button>
          </div>

          {/* Enhanced Tasks List */}
          <div className="divide-y divide-gray-100">
            {getFilteredTasks().length === 0 ? (
              <div className="p-12 text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
                  <Target className="h-8 w-8 text-gray-400" />
                </div>
                <p className="text-gray-500 font-medium">No tasks found</p>
                <p className="text-sm text-gray-400 mt-1">Create your first task to get started</p>
              </div>
            ) : (
              getFilteredTasks().slice(0, 10).map((task) => {
                const priorityColors = {
                  urgent: 'bg-red-100 text-red-700 border-red-200',
                  high: 'bg-orange-100 text-orange-700 border-orange-200',
                  medium: 'bg-yellow-100 text-yellow-700 border-yellow-200',
                  low: 'bg-blue-100 text-blue-700 border-blue-200'
                };
                
                return (
                  <div
                    key={task._id}
                    onClick={() => {
                      setSelectedTask(task);
                      setShowTaskDetailsModal(true);
                    }}
                    className="p-4 hover:bg-gradient-to-r hover:from-purple-50/50 hover:to-blue-50/50 transition-all cursor-pointer group border-b border-gray-100"
                  >
                    <div className="flex items-start gap-3">
                      <div className="mt-0.5 flex-shrink-0">{getTaskIcon(task)}</div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <h3 className="text-sm font-semibold text-gray-900 group-hover:text-purple-600 transition-colors">
                            {task.title}
                          </h3>
                          <span className={`text-xs px-2 py-0.5 rounded-full border font-medium flex-shrink-0 ${priorityColors[task.priority] || priorityColors.medium}`}>
                            {task.priority}
                          </span>
                        </div>
                        {task.description && (
                          <p className="text-sm text-gray-600 line-clamp-2">{task.description}</p>
                        )}
                        {!task.description && (
                          <p className="text-xs text-gray-400 italic">No description</p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Right Column - Projects and People */}
        <div className="col-span-12 lg:col-span-4 space-y-6">
          {/* Projects Section - Enhanced */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-lg hover:shadow-xl transition-shadow">
            <div className="border-b border-gray-200 p-5 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-t-2xl">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Briefcase className="h-5 w-5 text-blue-600" />
                  <h3 className="text-lg font-bold text-gray-900">Projects</h3>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => navigate('/calendar')}
                    className="p-2 hover:bg-white/50 rounded-lg transition-colors"
                    title="View calendar"
                  >
                    <CalendarIcon className="h-4 w-4 text-gray-600" />
                  </button>
                  <button className="p-2 hover:bg-white/50 rounded-lg transition-colors">
                    <MoreVertical className="h-5 w-5 text-gray-500" />
                  </button>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs px-2 py-1 bg-white/70 text-gray-600 rounded-md font-medium">
                  {projects.length} {projects.length === 1 ? 'project' : 'projects'}
                </span>
              </div>
            </div>

            <div className="p-5">
              <button
                onClick={() => setShowCreateProjectModal(true)}
                className="w-full flex items-center gap-3 p-4 border-2 border-dashed border-blue-300 rounded-xl hover:border-blue-400 hover:bg-blue-50/50 transition-all group"
              >
                <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-blue-100 to-indigo-100 flex items-center justify-center group-hover:from-blue-200 group-hover:to-indigo-200 transition-all">
                  <Plus className="h-6 w-6 text-blue-600" />
                </div>
                <div className="text-left">
                  <span className="text-sm font-semibold text-gray-900 block">Create project</span>
                  <span className="text-xs text-gray-500">Start a new project</span>
                </div>
              </button>

              {/* Enhanced Project Statistics */}
              {projects.length > 0 && (
                <div className="grid grid-cols-2 gap-3 mt-5">
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-3 text-center border border-blue-200 hover:shadow-md transition-shadow">
                    <BarChart2 className="h-5 w-5 text-blue-600 mx-auto mb-1" />
                    <p className="text-xs text-blue-700 font-medium mb-1">Meetings</p>
                    <p className="text-xl font-bold text-blue-900">{projectStats.meeting}</p>
                  </div>
                  <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-3 text-center border border-purple-200 hover:shadow-md transition-shadow">
                    <Briefcase className="h-5 w-5 text-purple-600 mx-auto mb-1" />
                    <p className="text-xs text-purple-700 font-medium mb-1">Workshops</p>
                    <p className="text-xl font-bold text-purple-900">{projectStats.workshop}</p>
                  </div>
                  <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-3 text-center border border-orange-200 hover:shadow-md transition-shadow">
                    <Star className="h-5 w-5 text-orange-600 mx-auto mb-1" />
                    <p className="text-xs text-orange-700 font-medium mb-1">Deadlines</p>
                    <p className="text-xl font-bold text-orange-900">{projectStats.deadline}</p>
                  </div>
                  <div className="bg-gradient-to-br from-pink-50 to-pink-100 rounded-xl p-3 text-center border border-pink-200 hover:shadow-md transition-shadow">
                    <Users className="h-5 w-5 text-pink-600 mx-auto mb-1" />
                    <p className="text-xs text-pink-700 font-medium mb-1">Social</p>
                    <p className="text-xl font-bold text-pink-900">{projectStats.social}</p>
                  </div>
                </div>
              )}

              {/* Projects List View */}
              <div className="mt-4 space-y-2 max-h-96 overflow-y-auto">
                  {projects.length === 0 ? (
                    <p className="text-sm text-gray-500 text-center py-4">No projects yet</p>
                  ) : (
                    projects.slice(0, 5).map((project) => (
                      <div
                        key={project._id}
                        onClick={() => handleProjectClick(project._id)}
                        className="group p-3 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer border border-gray-100"
                      >
                        <div className="flex items-start gap-2">
                          <div 
                            className="w-1 h-full min-h-[40px] rounded-full flex-shrink-0"
                            style={{ backgroundColor: project.color || '#3788d8' }}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="text-sm font-semibold text-gray-900 truncate">{project.title}</h4>
                              <span className={`px-1.5 py-0.5 rounded text-xs font-medium border ${getTypeColor(project.type)}`}>
                                {getTypeLabel(project.type)}
                              </span>
                            </div>
                            <p className="text-xs text-gray-500">{formatProjectDate(project.start)}</p>
                            {getTasksDueSoon(project) && (
                              <p className="text-xs text-orange-600 mt-1">{getTasksDueSoon(project)}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
          </div>
            </div>

        </div>
      </div>


      {/* Create Project Modal */}
      {showCreateProjectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="flex justify-between items-center border-b border-gray-200 p-4">
              <h3 className="text-lg font-semibold text-gray-800">New Project</h3>
              <button 
                onClick={() => setShowCreateProjectModal(false)} 
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <form onSubmit={handleCreateProject} className="p-6 space-y-4">
              <input
                name="title"
                placeholder="Title"
                required
                value={projectFormData.title}
                onChange={(e) => setProjectFormData({ ...projectFormData, title: e.target.value })}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <textarea
                name="description"
                placeholder="Description"
                value={projectFormData.description}
                onChange={(e) => setProjectFormData({ ...projectFormData, description: e.target.value })}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent h-20"
              />
              <input
                type="datetime-local"
                name="start"
                required
                value={projectFormData.start}
                onChange={(e) => setProjectFormData({ ...projectFormData, start: e.target.value })}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <input
                type="datetime-local"
                name="end"
                required
                value={projectFormData.end}
                onChange={(e) => setProjectFormData({ ...projectFormData, end: e.target.value })}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <select
                name="type"
                value={projectFormData.type}
                onChange={(e) => setProjectFormData({ ...projectFormData, type: e.target.value as any })}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="meeting">Meeting</option>
                <option value="workshop">Workshop</option>
                <option value="deadline">Deadline</option>
                <option value="social">Social</option>
              </select>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Color</label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    name="color"
                    value={projectFormData.color}
                    onChange={(e) => setProjectFormData({ ...projectFormData, color: e.target.value })}
                    className="w-16 h-10 border border-gray-300 rounded-lg cursor-pointer"
                  />
                  <input
                    type="text"
                    name="color"
                    value={projectFormData.color}
                    onChange={(e) => setProjectFormData({ ...projectFormData, color: e.target.value })}
                    placeholder="#3788d8"
                    className="flex-1 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    pattern="^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$"
                  />
                </div>
              </div>
              <label className="flex items-center space-x-2">
                <input
                  name="isPublic"
                  type="checkbox"
                  checked={projectFormData.isPublic}
                  onChange={(e) => setProjectFormData({ ...projectFormData, isPublic: e.target.checked })}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">Public</span>
              </label>
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-blue-500 to-purple-500 text-white py-2 rounded-lg hover:opacity-90 transition disabled:opacity-50"
              >
                {loading ? 'Creating...' : 'Create'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Create Task Modal */}
      {showCreateTaskModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
              <h3 className="text-xl font-bold text-gray-900">Create Task</h3>
              <button
                onClick={() => {
                  setShowCreateTaskModal(false);
                  setTaskFormData({
                    title: '',
                    description: '',
                    status: 'todo',
                    priority: 'medium',
                    deadline: '',
                    projectId: '',
                    teamId: '',
                    assigneeId: '',
                    tags: [],
                    subTasks: []
                  });
                  setTagsInput('');
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <form onSubmit={handleCreateTask} className="p-6 space-y-4">
              {/* Project Selection - Required */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Projet <span className="text-red-500">*</span>
                </label>
                <select
                  required
                  value={taskFormData.projectId}
                  onChange={async (e) => {
                    const projectId = e.target.value;
                    const project = projects.find(p => p._id === projectId);
                    
                    setTaskFormData({
                      ...taskFormData,
                      projectId: projectId,
                      teamId: project?.teams?.[0]?._id || '',
                      assigneeId: ''
                    });

                    if (projectId && !projectTeamsWithMembers[projectId]) {
                      try {
                        const token = localStorage.getItem('token');
                        const headers = { Authorization: `Bearer ${token}` };
                        const res = await axios.get(`${API_BASE}/projects/${projectId}`, { headers });
                        const projectWithTeams = res.data.data;
                        if (projectWithTeams?.teams) {
                          setProjectTeamsWithMembers(prev => ({
                            ...prev,
                            [projectId]: projectWithTeams.teams
                          }));
                        }
                      } catch (err) {
                        console.error('Error fetching project teams:', err);
                      }
                    }
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select a project</option>
                  {projects.map((project) => (
                    <option key={project._id} value={project._id}>
                      {project.title}
                    </option>
                  ))}
                </select>
              </div>

              {/* Titre */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Titre <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={taskFormData.title}
                  onChange={(e) => setTaskFormData({ ...taskFormData, title: e.target.value })}
                  placeholder="Task title"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Priorité */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
                  <select
                    value={taskFormData.priority}
                    onChange={(e) => setTaskFormData({ ...taskFormData, priority: e.target.value as any })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                  </select>
              </div>

              {/* Description */}
                <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <textarea
                  value={taskFormData.description}
                  onChange={(e) => setTaskFormData({ ...taskFormData, description: e.target.value })}
                  placeholder="Task description"
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

              {/* Date limite */}
                <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Due date</label>
                  <input
                    type="date"
                    value={taskFormData.deadline}
                    onChange={(e) => setTaskFormData({ ...taskFormData, deadline: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
              </div>

              {/* Assigné à */}
              {taskFormData.projectId && (() => {
                const cachedTeams = projectTeamsWithMembers[taskFormData.projectId];
                const selectedProject = projects.find(p => p._id === taskFormData.projectId);
                const projectTeam = cachedTeams?.[0] || selectedProject?.teams?.[0];
                const teamMembers = projectTeam?.members || [];
                
                return (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Assigned to</label>
                    <select
                      value={taskFormData.assigneeId}
                      onChange={(e) => setTaskFormData({ ...taskFormData, assigneeId: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Aucun</option>
                      {teamMembers.map((member: any) => {
                        const memberId = typeof member === 'object' ? member._id : member;
                        const memberName = typeof member === 'object' ? (member.name || member.email) : 'Unknown';
                        return (
                          <option key={memberId} value={memberId}>
                            {memberName}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                );
              })()}

              {/* Tags */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tags (comma-separated)
                </label>
                <input
                  type="text"
                  value={tagsInput}
                  onChange={(e) => setTagsInput(e.target.value)}
                  onBlur={() => {
                    if (tagsInput.trim()) {
                      const newTags = tagsInput
                        .split(',')
                        .map(tag => tag.trim())
                        .filter(tag => tag.length > 0 && !taskFormData.tags.includes(tag));
                      if (newTags.length > 0) {
                        setTaskFormData({
                          ...taskFormData,
                          tags: [...taskFormData.tags, ...newTags]
                        });
                        setTagsInput('');
                      }
                    }
                  }}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      if (tagsInput.trim()) {
                        const newTags = tagsInput
                          .split(',')
                          .map(tag => tag.trim())
                          .filter(tag => tag.length > 0 && !taskFormData.tags.includes(tag));
                        if (newTags.length > 0) {
                          setTaskFormData({
                            ...taskFormData,
                            tags: [...taskFormData.tags, ...newTags]
                          });
                          setTagsInput('');
                        }
                      }
                    }
                  }}
                  placeholder="ui, urgent"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
                {taskFormData.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {taskFormData.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm"
                      >
                        {tag}
                        <button
                          type="button"
                          onClick={() => {
                            setTaskFormData({
                              ...taskFormData,
                              tags: taskFormData.tags.filter((_, i) => i !== index)
                            });
                          }}
                          className="hover:text-blue-900"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                    )}
                  </div>

              {/* Sous-tâches */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Subtasks</label>
                <div className="space-y-2">
                  {taskFormData.subTasks.length === 0 ? (
                    <p className="text-sm text-gray-500">No subtasks</p>
                  ) : (
                    taskFormData.subTasks.map((subTask, index) => (
                      <div key={index} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                        <input
                          type="text"
                          value={subTask.title}
                          onChange={(e) => {
                            const updatedSubTasks = [...taskFormData.subTasks];
                            updatedSubTasks[index].title = e.target.value;
                            setTaskFormData({ ...taskFormData, subTasks: updatedSubTasks });
                          }}
                          placeholder="Subtask title"
                          className="flex-1 px-3 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setTaskFormData({
                              ...taskFormData,
                              subTasks: taskFormData.subTasks.filter((_, i) => i !== index)
                            });
                          }}
                          className="text-red-500 hover:text-red-700"
                        >
                          <X className="h-4 w-4" />
                        </button>
                  </div>
                    ))
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      setTaskFormData({
                        ...taskFormData,
                        subTasks: [...taskFormData.subTasks, { title: '', assigneeId: '', deadline: '' }]
                      });
                    }}
                    className="flex items-center gap-2 px-3 py-2 text-sm text-blue-600 hover:text-blue-700 font-medium"
                  >
                    <Plus className="h-4 w-4" />
                    Ajouter
                  </button>
                </div>
              </div>

              {/* Submit Button */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateTaskModal(false);
                    setTaskFormData({
                      title: '',
                      description: '',
                      status: 'todo',
                      priority: 'medium',
                      deadline: '',
                      projectId: '',
                      teamId: '',
                      assigneeId: '',
                      tags: [],
                      subTasks: []
                    });
                    setTagsInput('');
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm font-medium"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50"
                >
                  {loading ? 'Creating...' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Task Details Modal */}
      {showTaskDetailsModal && selectedTask && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
              <h3 className="text-xl font-bold text-gray-900">Task Details</h3>
              <button
                onClick={() => {
                  setShowTaskDetailsModal(false);
                  setSelectedTask(null);
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Title and Priority */}
              <div>
                <div className="flex items-start justify-between gap-4 mb-2">
                  <h2 className="text-2xl font-bold text-gray-900">{selectedTask.title}</h2>
                  <span className={`text-xs px-3 py-1.5 rounded-full border font-semibold flex-shrink-0 ${
                    selectedTask.priority === 'urgent' ? 'bg-red-100 text-red-700 border-red-200' :
                    selectedTask.priority === 'high' ? 'bg-orange-100 text-orange-700 border-orange-200' :
                    selectedTask.priority === 'medium' ? 'bg-yellow-100 text-yellow-700 border-yellow-200' :
                    'bg-blue-100 text-blue-700 border-blue-200'
                  }`}>
                    {selectedTask.priority}
                  </span>
                </div>
                {selectedTask.description && (
                  <p className="text-gray-600 mt-3 leading-relaxed">{selectedTask.description}</p>
                )}
              </div>

              {/* Status */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Status</label>
                <span className={`inline-block px-3 py-1.5 rounded-md font-medium text-sm ${
                  selectedTask.status === 'todo' ? 'bg-gray-100 text-gray-700' :
                  selectedTask.status === 'in-progress' ? 'bg-blue-100 text-blue-700' :
                  selectedTask.status === 'review' ? 'bg-yellow-100 text-yellow-700' :
                  selectedTask.status === 'done' ? 'bg-green-100 text-green-700' :
                  selectedTask.status === 'blocked' ? 'bg-red-100 text-red-700' :
                  'bg-gray-100 text-gray-500'
                }`}>
                  {selectedTask.status.replace('-', ' ')}
                </span>
              </div>

              {/* Project and Team */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {selectedTask.project?._id && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Project</label>
                    <button
                      onClick={() => {
                        navigate(`/projects/${selectedTask.project._id}`);
                        setShowTaskDetailsModal(false);
                      }}
                      className="w-full flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-100 to-blue-100 text-purple-700 rounded-lg font-medium hover:from-purple-200 hover:to-blue-200 transition-colors"
                    >
                      <FolderKanban className="h-4 w-4" />
                      {selectedTask.project?.title || 'Project'}
                    </button>
                  </div>
                )}
                {selectedTask.team && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Team</label>
                    <div className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg">
                      <Users className="h-4 w-4" />
                      <span className="font-medium">{selectedTask.team.name}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Dates and Duration */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {selectedTask.startDate && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Start Date</label>
                    <div className="flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-700 rounded-lg">
                      <CalendarIcon className="h-4 w-4" />
                      <span>{formatDate(selectedTask.startDate)}</span>
                    </div>
                  </div>
                )}
                {selectedTask.deadline && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Deadline</label>
                    <div className={`flex items-center gap-2 px-4 py-2 rounded-lg ${
                      new Date(selectedTask.deadline) < new Date() && selectedTask.status !== 'done'
                        ? 'bg-red-50 text-red-700'
                        : 'bg-orange-50 text-orange-700'
                    }`}>
                      <CalendarIcon className="h-4 w-4" />
                      <span>{formatDate(selectedTask.deadline)}</span>
                      {new Date(selectedTask.deadline) < new Date() && selectedTask.status !== 'done' && (
                        <AlertCircle className="h-4 w-4" />
                      )}
                    </div>
                  </div>
                )}
                {selectedTask.duration && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Duration</label>
                    <div className="flex items-center gap-2 px-4 py-2 bg-green-50 text-green-700 rounded-lg">
                      <Clock className="h-4 w-4" />
                      <span>{selectedTask.duration} {selectedTask.duration === 1 ? 'day' : 'days'}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Assignee */}
              {selectedTask.assignee && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Assignee</label>
                  <div className="flex items-center gap-3 px-4 py-3 bg-gray-50 rounded-lg">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 text-white text-sm flex items-center justify-center font-bold shadow-sm">
                      {selectedTask.assignee.name?.[0]?.toUpperCase() || 'U'}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{selectedTask.assignee.name}</p>
                      {selectedTask.assignee.email && (
                        <p className="text-sm text-gray-500">{selectedTask.assignee.email}</p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              {selectedTask.project?._id && (
                <div className="flex items-center gap-3 pt-4 border-t">
                  <button
                    onClick={() => {
                      navigate(`/projects/${selectedTask.project._id}`);
                      setShowTaskDetailsModal(false);
                    }}
                    className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                  >
                    <ExternalLink className="h-4 w-4" />
                    View Project
                  </button>
                  <button
                    onClick={() => {
                      navigate(`/projects/${selectedTask.project._id}/timeline`);
                      setShowTaskDetailsModal(false);
                    }}
                    className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium"
                  >
                    <GanttChart className="h-4 w-4" />
                    View Gantt
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
